import { observer } from "mobx-react-lite";
import { useFormikContext } from "formik";
import { createElement } from "react";

import { useTran } from "@/hooks";
import { SelectField } from "@/components/common";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuthStore } from "@/features/auth";
import { LANGUAGES } from "@/lib/i18n/languages";
import { REPORT_EXTRA_EXPORT_FORMATS } from "@/lib/constants/company-feature-settings";
import {
  applyFeatureSettingChoice,
  getCompanyFeatureDisplayDefaults,
  getFeatureSettingState,
} from "../utils/companyFeatureSettings";
import {
  COMPANY_FEATURE_SETTING_ROWS,
  FORMAT_LABEL_KEYS,
  describeFeatureSettingValue,
  featureSettingTitleId,
} from "../utils/companyFeatureSettingRows";

const HOST_DEFAULT = "host_default";

const hostDefaultOption = (row, defaults, t) => ({
  key: HOST_DEFAULT,
  label: defaults
    ? t("company_feature_host_default_value", {
        value: describeFeatureSettingValue(row, defaults[row.field], t),
      })
    : t("company_feature_host_default"),
});

const renderIcon = (Icon) =>
  createElement(Icon, { className: "h-4 w-4", "aria-hidden": true });

const SettingControl = ({ row, state, defaults, onChoose, t }) => {
  const titleId = featureSettingTitleId(row.field);
  // Host default is always offered: for a held value it clears it (null).
  const withHostDefault = (options) => [
    hostDefaultOption(row, defaults, t),
    ...options,
  ];

  if (row.kind === "boolean") {
    const current =
      state.mode === "default"
        ? HOST_DEFAULT
        : (row.inverted ? state.value !== true : state.value === true)
          ? "on"
          : "off";
    return (
      <SelectField
        ariaLabelledBy={titleId}
        options={withHostDefault([
          { key: "on", label: t("company_feature_on") },
          { key: "off", label: t("company_feature_off") },
        ])}
        value={current}
        sortOptions={false}
        onValueChange={(key) => {
          if (key === HOST_DEFAULT) return onChoose(undefined);
          const on = key === "on";
          onChoose(row.inverted ? !on : on);
        }}
      />
    );
  }

  if (row.kind === "language") {
    return (
      <SelectField
        ariaLabelledBy={titleId}
        options={withHostDefault(
          LANGUAGES.map((language) => ({
            key: language.code,
            label: language.nativeName,
          })),
        )}
        value={state.mode === "default" ? HOST_DEFAULT : state.value}
        sortOptions={false}
        onValueChange={(key) =>
          onChoose(key === HOST_DEFAULT ? undefined : key)
        }
      />
    );
  }

  // Formats: "Host default" or an explicit selection, which may be empty.
  const selected = state.mode === "explicit" ? state.value || [] : null;
  return (
    <div className="space-y-2">
      <SelectField
        ariaLabelledBy={titleId}
        options={withHostDefault([
          { key: "custom", label: t("company_feature_formats_choose") },
        ])}
        value={selected ? "custom" : HOST_DEFAULT}
        sortOptions={false}
        onValueChange={(key) => {
          if (key === HOST_DEFAULT) return onChoose(undefined);
          if (selected) return undefined;
          // Start from the held list when there is one, else the default.
          return onChoose([
            ...(state.held
              ? state.storedValue || []
              : defaults?.reportExportFormats || []),
          ]);
        }}
      />
      {selected && (
        <div className="flex flex-wrap gap-3">
          {REPORT_EXTRA_EXPORT_FORMATS.map((format) => (
            <label key={format} className="flex items-center gap-1.5 text-xs">
              <Checkbox
                checked={selected.includes(format)}
                onCheckedChange={(checked) =>
                  onChoose(
                    checked
                      ? [...selected, format]
                      : selected.filter((item) => item !== format),
                  )
                }
              />
              {t(FORMAT_LABEL_KEYS[format])}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// System admin only: the six per-company feature settings (WP4).
const CompanyFeatureSettingsFields = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  if (user?.role !== "system_admin") return null;

  const stored = values.featureSettingsStored || {};
  const edits = values.featureSettingEdits || {};
  const defaults = getCompanyFeatureDisplayDefaults(user);

  const choose = (field, value) => {
    setFieldValue(
      "featureSettingEdits",
      applyFeatureSettingChoice({ field, stored, edits, value }),
      false,
    );
    setFieldTouched("featureSettingEdits", true, false);
  };

  return (
    <div className="space-y-3" data-testid="company-feature-settings">
      {COMPANY_FEATURE_SETTING_ROWS.map((row) => (
        <div key={row.field} className="flex items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
            {renderIcon(row.Icon)}
          </span>
          <div className="min-w-0 flex-1">
            <p
              id={featureSettingTitleId(row.field)}
              className="text-xs font-semibold text-foreground"
            >
              {t(row.titleKey)}
            </p>
            <p className="text-[11px] leading-4 text-muted-foreground">
              {t(row.descriptionKey)}
            </p>
          </div>
          <div
            className="w-44 shrink-0"
            role="group"
            aria-labelledby={featureSettingTitleId(row.field)}
          >
            <SettingControl
              row={row}
              state={getFeatureSettingState({
                field: row.field,
                stored,
                edits,
              })}
              defaults={defaults}
              onChoose={(value) => choose(row.field, value)}
              t={t}
            />
          </div>
        </div>
      ))}
    </div>
  );
});

export default CompanyFeatureSettingsFields;
